export const THEMES = {
    modern: {
        id: "modern",
        label: "Modern",
        bg: "#f7f3ea",
        ink: "rgba(30,30,25,0.88)",
        inkFaint: "rgba(30,30,25,0.5)",
        rule: "rgba(30,30,25,0.16)",
        grain: 15,
        labelFont: (w) => `700 ${w * 0.024}px "Jost", sans-serif`,
        messageFont: (w, size = 0.026) => `italic ${w * size}px "Cormorant Garamond", serif`,
        handwriting: false,
        stampStyle: "boxed",
    },
    vintage: {
        id: "vintage",
        label: "Vintage",
        bg: "#e4d4ae",
        ink: "rgba(30,20,10,0.88)",
        inkFaint: "rgba(45,30,15,0.5)",
        rule: "rgba(45,30,15,0.22)",
        grain: 200,
        labelFont: null, // vintage uses drawVintageLabel
        messageFont: (w, size = 0.03) => `${w * size}px "Caveat", cursive`,
        handwriting: true,
        stampStyle: "perforated",
    }, 
    coastal: {
        id: "coastal",
        label: "Coastal",
        bg: "#f4f2ec",
        ink: "rgba(40,40,38,0.8)",
        inkFaint: "rgba(40,40,38,0.4)",
        rule: "rgba(40,40,38,0.14)",
        grain: 0,
        labelFont: (w) => `500 ${w * 0.02}px "Jost", sans-serif`,
        messageFont: (w, size = 0.02) => `${w * size}px "Jost", sans-serif`,
        handwriting: false,
        stampStyle: "dashed",
    },
};

export const BACK_ZONES = {
    message: { left: 0.05, top: 0.12, width: 0.46, height: 0.6 },
    sender: { left: 0.05, top: 0.76, width: 0.46, height: 0.14 },
    recipient: { left: 0.58, top: 0.42, width: 0.37, height: 0.08 },
    address: { left: 0.58, top: 0.5, width: 0.37, height: 0.32 },
};

// ============================================================
// FRONT
// ============================================================
export async function generatePostcard({ canvas, photoUrl, location = "", date = "", title = "", creator = "", themeId = "vintage" }) {
    const theme = THEMES[themeId] || THEMES.vintage;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const photo = await loadImage(photoUrl);

    const year = (date && new Date(date).getFullYear()) || new Date().getFullYear();
    const creditName = creator && creator.trim() ? creator : "Winston.ie";

    if (theme.id === "modern") {
        ctx.fillStyle = theme.bg;
        ctx.fillRect(0, 0, W, H);
        const border = W * 0.035;
        const labelH = H * 0.09;
        drawCover(ctx, photo, border, border + labelH, W - border * 2, H - border * 2 - labelH * 1.6);

        ctx.fillStyle = theme.ink;
        ctx.font = `700 ${W * 0.026}px "Jost", sans-serif`;
        drawLetterSpaced(ctx, (title || location || "WINSTON.IE").toUpperCase(), border, border + labelH * 0.6, W * 0.003);

        ctx.textAlign = "right";
        ctx.font = `${W * 0.016}px "Jost", sans-serif`;
        ctx.fillStyle = theme.inkFaint;
        ctx.fillText(formatMonthYear(date) || String(year), W - border, border + labelH * 0.6);
        ctx.textAlign = "left";

        ctx.fillStyle = theme.inkFaint;
        ctx.font = `${W * 0.013}px "Jost", sans-serif`;
        ctx.textAlign = "right";
        ctx.fillText(`© ${creditName} — winston.ie ${year}`, W - border, H - border * 0.4);
        ctx.textAlign = "left";
    } else if (theme.id === "coastal") {
        drawCover(ctx, photo, 0, 0, W, H);
        const scrim = ctx.createLinearGradient(0, H * 0.85, 0, H);
        scrim.addColorStop(0, "rgba(20,20,18,0)");
        scrim.addColorStop(1, "rgba(20,20,18,0.4)");
        ctx.fillStyle = scrim;
        ctx.fillRect(0, H * 0.85, W, H * 0.15);
        
        if (title) {
            ctx.fillStyle = "rgba(255,255,255,0.95)";
            ctx.font = `300 ${W * 0.02}px "Jost", sans-serif`;
            ctx.fillText(title, W * 0.04, H * 0.9);
        }
        if (location) {
            ctx.fillStyle = "rgba(255,255,255,0.75)";
            ctx.font = `${W * 0.013}px "Jost", sans-serif`;
            drawLetterSpaced(ctx, location.toUpperCase(), W * 0.04, H * 0.94, W * 0.0025);
        }
        ctx.fillStyle = "rgba(255,255,255,0.6)";
        ctx.font = `${W * 0.011}px "Jost", sans-serif`;
        ctx.textAlign = "right";
        ctx.fillText(`© ${creditName} ${year}`, W * 0.96, H * 0.94);
        ctx.textAlign = "left";
    } else {
        drawCover(ctx, photo, 0, 0, W, H);
        ctx.fillStyle = "rgba(90,60,30,0.08)";
        ctx.fillRect(0, 0, W, H);

        const grad = ctx.createLinearGradient(0, H * 0.72, 0, H);
        grad.addColorStop(0, "rgba(14,16,19,0)");
        grad.addColorStop(1, "rgba(14,16,19,0.55)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, H * 0.72, W, H * 0.28);

        if (title) {
            ctx.fillStyle = "#f4f1ec";
            ctx.font = `italic ${W * 0.032}px "Cormorant Garamond", serif`;
            ctx.fillText(title, W * 0.045, H * 0.94);
        }
        if (location) {
            ctx.fillStyle = "rgba(244,241,236,0.75)";
            ctx.font = `${W * 0.014}px "Jost", sans-serif`;
            ctx.fillText(location.toUpperCase(), W * 0.045, H * 0.965);
        }
        ctx.fillStyle = "rgba(244,241,236,0.55)";
        ctx.font = `${W * 0.012}px "Jost", sans-serif`;
        ctx.textAlign = "right";
        ctx.fillText(`© ${creditName} — winston.ie ${year}`, W * 0.955, H * 0.965);
        ctx.textAlign = "left";
    }

    return canvas;
}

// ============================================================
// BACK
// ============================================================
export function generatePostcardBack(
    canvas,
    { message = "", senderName = "", recipientName = "", address = "", creator = "", date = "", stampImg = null, themeId = "vintage" }
) {
    const theme = THEMES[themeId] || THEMES.vintage;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;

    ctx.fillStyle = theme.bg;
    ctx.fillRect(0, 0, W, H);
    if (theme.grain > 0) drawGrain(ctx, 0, 0, W, H, theme.grain);
    if (theme.id === "vintage") {
        const vign = ctx.createRadialGradient(W / 2, H / 2, W * 0.1, W / 2, H / 2, W * 0.8);
        vign.addColorStop(0, "rgba(90,60,20,0)");
        vign.addColorStop(1, "rgba(70,45,15,0.22)");
        ctx.fillStyle = vign;
        ctx.fillRect(0, 0, W, H);
    }

    const margin = W * 0.05;
    const dividerX = W * 0.55;

    if (theme.id === "vintage") {
        drawVintageLabel(ctx, "POST CARD.", margin, margin + H * 0.035, W * 0.026);
    } else {
        ctx.fillStyle = theme.ink;
        ctx.font = theme.labelFont(W);
        drawLetterSpaced(ctx, "POST CARD.", margin, margin + H * 0.02, W * 0.004);
    }

    ctx.strokeStyle = theme.rule;
    ctx.lineWidth = W * 0.0012;
    ctx.beginPath();
    ctx.moveTo(dividerX, margin + H * 0.05);
    ctx.lineTo(dividerX, H - margin);
    ctx.stroke();

    const msgTop = margin + H * 0.1;
    const msgBottom = H * 0.72;
    const lineGap = H * 0.075;

    ctx.strokeStyle = theme.rule;
    ctx.lineWidth = W * 0.0009;
    let ly = msgTop;
    while (ly < msgBottom) {
        ctx.beginPath();
        ctx.moveTo(margin, ly);
        ctx.lineTo(dividerX - margin * 0.4, ly);
        ctx.stroke();
        ly += lineGap;
    }

    const maxWidth = dividerX - margin * 1.4;
    const availableLines = Math.max(1, Math.floor((msgBottom - msgTop) / lineGap));

    // shrink the message font step by step until the wrapped text fits the ruled lines available
    let msgSize = theme.handwriting ? 0.03 : 0.026;
    let msgLines = wrapText(ctx, message, maxWidth, theme.messageFont(W, msgSize));
    while (msgLines.length > availableLines && msgSize > 0.012) {
        msgSize -= 0.002;
        msgLines = wrapText(ctx, message, maxWidth, theme.messageFont(W, msgSize));
    }
    // if it still overflows even at minimum size, hard-truncate so nothing spills into the sender area
    if (msgLines.length > availableLines) {
        msgLines = msgLines.slice(0, availableLines);
        const last = msgLines[availableLines - 1];
        msgLines[availableLines - 1] = last.length > 3 ? last.slice(0, -3) + "…" : last;
    }

    ctx.fillStyle = theme.ink;
    msgLines.forEach((line, i) => {
        ctx.save();
        let baseY = msgTop + lineGap * (i + 1) - lineGap * 0.22;
        ctx.font = theme.messageFont(W, msgSize);
        if (theme.handwriting) {
            const jitter = (pseudoRandom(i, 3.4) - 0.5) * H * 0.006;
            baseY += jitter;
            ctx.translate(margin, baseY);
            ctx.rotate((pseudoRandom(i, 8.1) - 0.5) * 0.03);
            ctx.font = theme.messageFont(W, msgSize + pseudoRandom(i, 1.2) * 0.004);
            ctx.fillText(line, 0, 0);
        } else {
            ctx.fillText(line, margin, baseY);
        }
        ctx.restore();
    });

    if (senderName) {
        ctx.fillStyle = theme.inkFaint;
        ctx.font = theme.messageFont(W, theme.handwriting ? 0.026 : 0.017);
        ctx.fillText(theme.handwriting ? senderName : `— ${senderName}`, margin, H * 0.82);
    }

    const stampW = W * 0.1;
    const stampH = stampW * 1.2;
    const stampX = W - margin - stampW;
    const stampY = margin + H * 0.03;
    drawStamp(ctx, theme, stampImg, stampX, stampY, stampW, stampH);

    const addrTop = stampY + stampH + H * 0.09;
    const fieldGap = H * 0.055;
    const MAX_ADDR_LINES = 5;

    ctx.strokeStyle = theme.rule;
    ctx.lineWidth = W * 0.0009;
    for (let i = 0; i < MAX_ADDR_LINES; i++) {
        const ry = addrTop + fieldGap * i;
        ctx.beginPath();
        ctx.moveTo(dividerX + margin * 0.6, ry);
        ctx.lineTo(W - margin, ry);
        ctx.stroke();
    }

    ctx.fillStyle = theme.ink;
    ctx.font = theme.messageFont(W, theme.handwriting ? 0.026 : 0.019);
    ctx.fillText(recipientName || "", dividerX + margin * 0.6, addrTop - fieldGap * 0.22);

    const addrLines = (address || "")
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .slice(0, MAX_ADDR_LINES - 1);

    addrLines.forEach((line, i) => {
        const ry = addrTop + fieldGap * (i + 1);
        ctx.fillText(line, dividerX + margin * 0.6, ry - fieldGap * 0.22);
    });

    // small corner brand mark, bottom-right, distinct from the stamp — true aspect ratio, contain-fit
    if (stampImg) {
        const markBox = W * 0.032;
        const ratio = stampImg.width / stampImg.height;
        let markW = markBox, markH = markBox / ratio;
        if (markH > markBox) {
            markH = markBox;
            markW = markBox * ratio;
        }
        ctx.save();
        ctx.globalAlpha = 0.35;
        ctx.filter = theme.id === "coastal" ? "invert(0) brightness(0.2)" : "sepia(1) saturate(150%) brightness(45%)";
        ctx.drawImage(
            stampImg,
            W - margin - markW,
            H - margin - markH * 1.6,
            markW,
            markH
        );
        ctx.restore();
    }

    const year = (date && new Date(date).getFullYear()) || new Date().getFullYear();
    ctx.fillStyle = theme.inkFaint;
    ctx.font = `${W * 0.011}px "Jost", sans-serif`;
    ctx.textAlign = "right";
    ctx.fillText(`© ${creator || "Winston.ie"} — winston.ie ${year}`, W - margin - W * 0.035, H - margin * 0.4);
    ctx.textAlign = "left";

    return canvas;
}

function drawStamp(ctx, theme, logoImg, x, y, w, h) {
    if (theme.stampStyle === "dashed" || !logoImg) {
        ctx.save();
        ctx.setLineDash([w * 0.06, w * 0.045]);
        ctx.strokeStyle = theme.rule;
        ctx.lineWidth = w * 0.015;
        ctx.strokeRect(x, y, w, h);
        ctx.restore();
        return;
    }

    if (theme.stampStyle === "perforated") {
        const bump = w * 0.06;
        const spacing = bump * 1.8;
        ctx.save();
        ctx.beginPath();
        perforatedPath(ctx, x, y, w, h, bump, spacing);
        ctx.closePath();
        ctx.fillStyle = "#f0e6cc";
        ctx.shadowColor = "rgba(0,0,0,0.3)";
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.clip();
        drawGrain(ctx, x, y, w, h, 30);
        const pad = w * 0.28;
        ctx.strokeStyle = "rgba(45,30,15,0.45)";
        ctx.lineWidth = w * 0.015;
        ctx.strokeRect(x + pad * 0.5, y + pad * 0.5, w - pad, h - pad);
        drawLogoSepia(ctx, logoImg, x, y, w, h, pad);
        ctx.restore();
        return;
    }

    ctx.save();
    ctx.strokeStyle = theme.rule.replace(/[\d.]+\)$/, "0.6)");
    ctx.lineWidth = w * 0.01;
    ctx.strokeRect(x, y, w, h);
    const pad = w * 0.2;
    drawLogoSepia(ctx, logoImg, x, y, w, h, pad, false);
    ctx.restore();
}

function drawLogoSepia(ctx, logoImg, x, y, w, h, pad, sepia = true) {
    const logoRatio = logoImg.width / logoImg.height;
    const innerW = w - pad * 1.6;
    let logoW = innerW, logoH = innerW / logoRatio;
    if (logoH > h - pad * 1.6) {
        logoH = h - pad * 1.6;
        logoW = logoH * logoRatio;
    }
    ctx.save();
    if (sepia) ctx.filter = "sepia(1) saturate(150%) brightness(45%)";
    else ctx.filter = "invert(1) brightness(0.15)";
    ctx.drawImage(logoImg, x + (w - logoW) / 2, y + (h - logoH) / 2, logoW, logoH);
    ctx.restore();
}

// stretched, tall serif label — old-postcard letterform proportions
function drawVintageLabel(ctx, text, x, y, sizePx) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(1, 1.35);
    ctx.fillStyle = "rgba(45,30,15,0.85)";
    ctx.font = `700 ${sizePx}px "Cormorant Garamond", serif`;
    ctx.textAlign = "left";
    let cx = 0;
    for (const ch of text) {
        ctx.fillText(ch, cx, 0);
        cx += ctx.measureText(ch).width + sizePx * 0.06;
    }
    ctx.restore();
}

// ============================================================
// SHARED HELPERS
// ============================================================
function loadImage(src) {
    return fetch(src, { mode: "cors" })
        .then((res) => {
            if (!res.ok) throw new Error("fetch failed");
            return res.blob();
        })
        .then(
            (blob) =>
                new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => resolve(img);
                    img.onerror = reject;
                    img.src = URL.createObjectURL(blob);
                })
        )
        .catch(
            () =>
                new Promise((resolve, reject) => {
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.onload = () => resolve(img);
                    img.onerror = reject;
                    img.src = src;
                })
        );
}

function drawCover(ctx, img, x, y, w, h) {
    const imgRatio = img.width / img.height;
    const rectRatio = w / h;
    let sx, sy, sw, sh;
    if (imgRatio > rectRatio) {
        sh = img.height; sw = sh * rectRatio; sx = (img.width - sw) / 2; sy = 0;
    } else {
        sw = img.width; sh = sw / rectRatio; sx = 0; sy = (img.height - sh) / 2;
    }
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function perforatedPath(ctx, x, y, w, h, bump, spacing) {
    ctx.moveTo(x, y);
    scallopEdge(ctx, x, y, x + w, y, bump, spacing);
    scallopEdge(ctx, x + w, y, x + w, y + h, bump, spacing);
    scallopEdge(ctx, x + w, y + h, x, y + h, bump, spacing);
    scallopEdge(ctx, x, y + h, x, y, bump, spacing);
}

function scallopEdge(ctx, x1, y1, x2, y2, bump, spacing) {
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    const steps = Math.max(2, Math.round(len / spacing));
    const stepLen = len / steps;
    const ux = dx / len, uy = dy / len;
    const nx = -uy, ny = ux;
    for (let i = 0; i < steps; i++) {
        const sx = x1 + ux * stepLen * i, sy = y1 + uy * stepLen * i;
        const mx = sx + ux * stepLen * 0.5 + nx * bump, my = sy + uy * stepLen * 0.5 + ny * bump;
        const ex = x1 + ux * stepLen * (i + 1), ey = y1 + uy * stepLen * (i + 1);
        ctx.quadraticCurveTo(mx, my, ex, ey);
    }
}

function pseudoRandom(a, b) {
    const s = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
    return s - Math.floor(s);
}

function drawGrain(ctx, x, y, w, h, count) {
    ctx.save();
    for (let i = 0; i < count; i++) {
        const gx = x + pseudoRandom(i, 1.7) * w;
        const gy = y + pseudoRandom(i, 4.3) * h;
        const size = 0.5 + pseudoRandom(i, 9.1) * 1;
        ctx.fillStyle = `rgba(80,55,30,${0.03 + pseudoRandom(i, 2.2) * 0.05})`;
        ctx.beginPath();
        ctx.arc(gx, gy, size, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.restore();
}

function drawLetterSpaced(ctx, text, x, y, spacing) {
    let cx = x;
    for (const ch of text) {
        ctx.fillText(ch, cx, y);
        cx += ctx.measureText(ch).width + spacing;
    }
}

function wrapText(ctx, text, maxWidth, font) {
    ctx.font = font;
    const paragraphs = (text || "").split(/\r?\n/); // respect manual line breaks first
    const lines = [];

    paragraphs.forEach((paragraph) => {
        if (paragraph === "") {
            lines.push(""); // preserve blank lines from double-Enter
            return;
        }

        const words = paragraph.split(" ");
        let current = "";

        words.forEach((word) => {
            if (ctx.measureText(word).width > maxWidth) {
                if (current) {
                    lines.push(current);
                    current = "";
                }
                let chunk = "";
                for (const ch of word) {
                    const test = chunk + ch;
                    if (ctx.measureText(test).width > maxWidth && chunk) {
                        lines.push(chunk);
                        chunk = ch;
                    } else {
                        chunk = test;
                    }
                }
                current = chunk;
                return;
            }

            const test = current ? `${current} ${word}` : word;
            if (ctx.measureText(test).width > maxWidth && current) {
                lines.push(current);
                current = word;
            } else {
                current = test;
            }
        });

        if (current) lines.push(current);
    });

    return lines;
}

function formatMonthYear(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return "";
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase();
}